import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wordmark } from "@/components/common/Wordmark";
import { LoadingState, Notice, Spinner } from "@/components/common/States";
import { api, errorMessage } from "@/lib/api";

const tokenFromHash = () => new URLSearchParams(window.location.hash.slice(1)).get("t") || "";

const Field = ({ id, label, ...props }) => (
  <div className="space-y-2">
    <Label htmlFor={id}>{label}</Label>
    <Input id={id} {...props} />
  </div>
);

export default function ResetPasswordPage() {
  const [token] = useState(tokenFromHash);
  const [info, setInfo] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoadErr("Tautan reset tidak lengkap.");
      return;
    }
    api.get("/auth/reset-password/verify", { params: { token } })
      .then((r) => {
        if (r.data.valid) setInfo(r.data);
        else setLoadErr(r.data.message || "Tautan reset tidak valid atau sudah kedaluwarsa.");
      })
      .catch((e) => setLoadErr(errorMessage(e)));
  }, [token]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 10) return setErr("Kata sandi baru minimal 10 karakter.");
    if (!/[A-Za-z]/.test(form.password) || !/\d/.test(form.password)) {
      return setErr("Kata sandi baru harus memuat huruf dan angka.");
    }
    if (form.password !== form.confirm) return setErr("Ketik ulang kata sandi dengan tepat sama.");
    setBusy(true);
    setErr("");
    try {
      await api.post("/auth/reset-password", { token, password: form.password });
      setDone(true);
    } catch (ex) {
      setErr(errorMessage(ex));
    } finally {
      setBusy(false);
    }
  };

  const body = () => {
    if (loadErr) {
      return (
        <div className="space-y-4">
          <Notice tone="danger" testId="reset-load-error">{loadErr}</Notice>
          <Link to="/masuk" className="text-sm font-semibold text-[#3B82F6] hover:underline" data-testid="reset-back-login">
            Kembali ke halaman masuk
          </Link>
        </div>
      );
    }
    if (!info) return <LoadingState rows={3} />;
    if (done) {
      return (
        <div className="space-y-4 text-center" data-testid="reset-success">
          <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" aria-hidden />
          <p className="font-semibold">Kata sandi berhasil diatur ulang.</p>
          <p className="text-sm text-muted-foreground">Silakan masuk kembali dengan kata sandi baru Anda.</p>
          <Button asChild data-testid="reset-go-login"><Link to="/masuk">Masuk sekarang</Link></Button>
        </div>
      );
    }
    return (
      <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form" noValidate>
        <div className="rounded-lg bg-muted p-4 text-sm space-y-1">
          <p><span className="text-muted-foreground">Akun:</span> <b data-testid="reset-account-email">{info.email}</b></p>
          {info.full_name && <p><span className="text-muted-foreground">Nama:</span> {info.full_name}</p>}
        </div>
        <Field id="pw" label="Kata sandi baru" type="password" autoComplete="new-password"
          value={form.password} onChange={set("password")} placeholder="Minimal 10 karakter, huruf dan angka"
          data-testid="reset-password-input" />
        <Field id="pw2" label="Ketik ulang kata sandi baru" type="password" autoComplete="new-password"
          value={form.confirm} onChange={set("confirm")} data-testid="reset-password-confirm" />
        {err && <p className="text-sm text-[#E11D2E]" role="alert" data-testid="reset-error">{err}</p>}
        <Button type="submit" className="w-full" disabled={busy} data-testid="reset-password-submit">
          {busy && <Spinner />} Simpan kata sandi baru
        </Button>
      </form>
    );
  };

  return (
    <div className="min-h-screen grid place-items-center p-6">
      <div className="w-full max-w-md space-y-6 sist-rise">
        <Wordmark />
        <div className="flex items-center gap-2">
          <KeyRound className="h-6 w-6 text-[#E11D2E]" aria-hidden />
          <h1 className="font-brand text-3xl font-extrabold">Atur Ulang Kata Sandi</h1>
        </div>
        {body()}
      </div>
    </div>
  );
}
